"use client";

import React from "react";
import Image from "next/image";
import { Section, Typography, Button } from "@/components/ui";
import Link from "next/link";

export interface HowToStep {
  stepNumber: number;
  icon: string;
  iconAlt: string;
  title: string;
  description: string;
  image: {
    src: string;
    alt: string;
    width?: number;
    height?: number;
  };
}

interface HowToStepsSectionProps {
  title?: string;
  subtitle?: string;
  steps: HowToStep[];
  ctaText?: string;
  ctaHref?: string;
  background?: string;
  className?: string;
}

export function HowToStepsSection({
  title = "How-To-Steps",
  subtitle = " ",
  steps,
  ctaText = "Learn how to setup works",
  ctaHref = "#",
  background = "bg-[#1E3A8A]",
  className = "",
}: HowToStepsSectionProps) {
  return (
    <Section variant="default" background="transparent" className={`${background} ${className}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Typography variant="h2" as="h2" className="mb-6 text-text-primary">
            {title}
          </Typography>
          <Typography variant="body" as="p" className="text-text-secondary max-w-4xl mx-auto text-base leading-relaxed">
            {subtitle}
          </Typography>
        </div>

        {/* Step Details with Images */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-7 mb-12">
          {steps.map((step, index) => (
            <div
              key={index}
              className="flex flex-col items-start text-start h-full"
            >
              {/* Title */}
              <Typography variant="h3" as="h3" className="text-text-primary font-bold text-xl mb-4">
                {step.title}
              </Typography>

              {/* Description */}
              <Typography variant="body" as="p" className="text-text-secondary text-sm leading-relaxed min-h-[92px] mb-8">
                {step.description}
              </Typography>

              {/* Image - Displayed directly below each step */}
              <div className="relative w-full bg-[#FFF2E6] rounded-lg overflow-hidden mt-auto">
                <Image
                  src={step.image.src}
                  alt={step.image.alt}
                  title={step.image.alt}
                  width={step.image.width || 1200}
                  height={step.image.height || 700}
                  className="w-full h-auto"
                  priority={index === 0}
                />
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="flex justify-center">
          <Link
            data-mixpanel-id="cta-start-free-trial-how-to-steps"
            href={ctaHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="primary" size="md" asChild>
              {ctaText}
            </Button>
          </Link>
        </div>
      </div>
    </Section>
  );
}

